import { Box, Paper, alpha, useTheme, keyframes, BoxProps } from "@mui/material"
import { ReactNode } from "react"

interface uploadStatusBoxProps {
	children: ReactNode
	icon: ReactNode
	iconColor: string
	variant?: "loading" | "success" | "error" | "default"
}

// Define animations
const pulse = keyframes`
  0% {
    opacity: 0.6;
    transform: scale(0.98);
  }
  50% {
    opacity: 1;
    transform: scale(1);
  }
  100% {
    opacity: 0.6;
    transform: scale(0.98);
  }
`

const float = keyframes`
  0% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
  100% {
    transform: translateY(0px);
  }
`

const shine = keyframes`
  0% {
    background-position: -100% 0;
  }
  100% {
    background-position: 200% 0;
  }
`

export function UploadStatusBox({
	children,
	icon,
	iconColor,
	variant = "default",
}: uploadStatusBoxProps) {
	const theme = useTheme()

	// Determine styles based on variant
	const getContainerStyles = () => {
		const baseStyles = {
			position: "relative",
			display: "flex",
			flexDirection: "column",
			alignItems: "center",
			justifyContent: "center",
			minHeight: "75vh",
			borderRadius: 3,
			p: 6,
			overflow: "hidden",
			transition: "all 0.3s ease",
		}

		switch (variant) {
			case "loading":
				return {
					...baseStyles,
					"backgroundColor": "transparent",
					"backgroundImage": `
            radial-gradient(circle at top left, ${alpha(theme.palette.primary.light, 0.08)}, transparent 40%),
            radial-gradient(circle at bottom right, ${alpha(theme.palette.secondary.light, 0.08)}, transparent 40%)
          `,
					"boxShadow": `0 12px 40px ${alpha(theme.palette.primary.main, 0.15)}`,
					"&::before": {
						content: '""',
						position: "absolute",
						top: 0,
						left: 0,
						right: 0,
						height: "4px",
						background: `linear-gradient(90deg, 
              ${alpha(theme.palette.primary.main, 0)}, 
              ${theme.palette.primary.main}, 
              ${theme.palette.secondary.main}, 
              ${alpha(theme.palette.secondary.main, 0)})`,
						backgroundSize: "200% 100%",
						animation: `${shine} 3s infinite linear`,
						zIndex: 1,
					},
					"&::after": {
						content: '""',
						position: "absolute",
						bottom: 0,
						left: 0,
						right: 0,
						height: "1px",
						background: alpha(theme.palette.divider, 0.3),
					},
				}
			case "success":
				return {
					...baseStyles,
					backgroundColor: alpha(theme.palette.success.main, 0.03),
					backgroundImage: `radial-gradient(circle at top left, ${alpha(theme.palette.success.light, 0.2)}, transparent 50%)`,
					boxShadow: `0 16px 40px ${alpha(theme.palette.success.main, 0.2)}`,
					border: `1px solid ${alpha(theme.palette.success.main, 0.1)}`,
				}
			case "error":
				return {
					...baseStyles,
					backgroundColor: alpha(theme.palette.error.main, 0.03),
					backgroundImage: `radial-gradient(circle at bottom right, ${alpha(theme.palette.error.light, 0.2)}, transparent 50%)`,
					boxShadow: `0 16px 40px ${alpha(theme.palette.error.main, 0.2)}`,
					border: `1px solid ${alpha(theme.palette.error.main, 0.1)}`,
				}
			default:
				return {
					...baseStyles,
					backgroundColor:
						theme.palette.mode === "dark"
							? alpha(theme.palette.background.paper, 0.8)
							: theme.palette.background.paper,
					boxShadow: `0 12px 32px ${alpha("#000", 0.1)}`,
					border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
				}
		}
	}

	const getIconContainerStyles = (): BoxProps["sx"] => {
		const baseStyles = {
			display: "flex",
			justifyContent: "center",
			alignItems: "center",
			mb: 4,
			position: "relative",
			zIndex: 2,
		}

		switch (variant) {
			case "loading":
				return {
					...baseStyles,
					"animation": `${float} 3s ease-in-out infinite`,
					"&::after": {
						content: '""',
						position: "absolute",
						width: "140px",
						height: "20px",
						bottom: "-30px",
						background: `radial-gradient(ellipse at center, ${alpha(theme.palette.primary.main, 0.2)} 0%, ${alpha(theme.palette.primary.main, 0)} 70%)`,
						borderRadius: "50%",
						animation: `${pulse} 3s ease-in-out infinite`,
					},
				}
			case "success":
			case "error":
				return {
					...baseStyles,
					"position": "relative",
					"&::after": {
						content: '""',
						position: "absolute",
						width: "180px",
						height: "30px",
						bottom: "-20px",
						background: `radial-gradient(ellipse at center, ${alpha(iconColor || theme.palette.primary.main, 0.15)} 0%, rgba(0,0,0,0) 70%)`,
						borderRadius: "50%",
					},
				}
			default:
				return baseStyles
		}
	}

	return (
		<Paper elevation={0} sx={getContainerStyles()}>
			{/* Background elements for loading state */}
			{variant === "loading" && (
				<>
					<Box
						sx={{
							position: "absolute",
							top: "-80px",
							right: "-80px",
							width: "200px",
							height: "200px",
							background: `radial-gradient(circle, ${alpha(theme.palette.primary.light, 0.04)}, transparent 70%)`,
							zIndex: 0,
						}}
					/>
					<Box
						sx={{
							position: "absolute",
							bottom: "-100px",
							left: "-100px",
							width: "250px",
							height: "250px",
							background: `radial-gradient(circle, ${alpha(theme.palette.secondary.light, 0.04)}, transparent 70%)`,
							zIndex: 0,
						}}
					/>
				</>
			)}

			{/* Icon container */}
			<Box sx={getIconContainerStyles()}>{icon}</Box>

			{/* Content container */}
			<Box
				sx={{
					position: "relative",
					zIndex: 2,
					maxWidth: "800px",
					width: "100%",
					textAlign: "center",
					px: 2,
				}}
			>
				{children}
			</Box>
		</Paper>
	)
}
